import "../index.css";

import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { type ReactElement, StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { ApiError } from "@/shared/lib/errors";

import { routeTree } from "../routeTree.gen";

Sentry.init({
  dsn: "https://d369427ce8fe16ca1b92eafe3da22020@o4510765001801728.ingest.us.sentry.io/4510765003309056",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  enabled: !import.meta.env.DEV,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Create a new router instance
const router = createRouter({ routeTree });

// Create a QueryClient instance with cancellation support
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Skip cancelled requests
      if (
        error.name === "CanceledError" ||
        (error instanceof ApiError && error.code === "ERR_CANCELED")
      ) {
        return;
      }

      // Skip 401 errors (user not authenticated)
      if (error instanceof ApiError && error.status === 401) {
        return;
      }

      // Extract query key info for tagging
      const [keyType] = query.queryKey as [string, ...unknown[]];

      Sentry.withScope((scope) => {
        // Set appropriate error level
        scope.setLevel(
          error instanceof ApiError && error.status && error.status < 500 ? "warning" : "error"
        );

        // Add tags for filtering in Sentry
        scope.setTags({
          "query.key": keyType,
          "error.type": error.constructor.name,
        });

        // Add query context
        scope.setContext("query", {
          queryKey: query.queryKey,
          queryHash: query.queryHash,
        });

        // For API errors, add response details
        if (error instanceof ApiError) {
          scope.setTags({
            "api.status": error.status,
          });
          scope.setContext("api_error", {
            status: error.status,
            message: error.message,
            code: error.code,
          });
        }

        // Set fingerprint for better grouping
        scope.setFingerprint([
          keyType,
          error instanceof ApiError ? String(error.status) : "unknown",
          error.message,
        ]);

        Sentry.captureException(error);
      });
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 30_000, // Consider data fresh for 30s to avoid unnecessary refetches
    },
  },
});

function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
}

// Render the app
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element with id 'root' not found");
}
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}
